import React, { useLayoutEffect, useMemo, useRef } from "react";
import styles from "./ResourcesPlanner.module.scss";
import CreateEditTask from "../scopeOverview/CreateEditTask";
import { UsersIcon } from "../dashboard/icons/icons";
import CustomButton from "../../components/controls/button/Button";
import ConflictsToast from "./components/ConflictsToast";
import FloatingAllocationCard from "./components/FloatingAllocationCard";
import PlannerHeader from "./components/PlannerHeader";
import PlannerLeftPanel from "./components/PlannerLeftPanel";
import PlannerTimeline from "./components/PlannerTimeline";
import PlannerResourceInsights from "./components/PlannerResourceInsights";
import { countWeekdays, isoToDay } from "./engine/dayMath";
import useViewportSync from "./hooks/useViewportSync";
import PlannerProvider from "./PlannerProvider";
import RowModelProvider from "./RowModelProvider";
import { useData, usePlannerEngine, useUi, useView } from "./plannerContext";

/** What stands in for the planner while the first page is on its way: the shape of the rows, not a spinner. */
function LoadingSkeleton() {
  const panelWidth = useView((state) => state.panelWidth);
  const rows = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <div className={styles.scrollWrapper}>
      <div className={styles.scrollInner}>
        <div className={styles.leftPanel} style={{ width: panelWidth, minWidth: panelWidth, maxWidth: panelWidth }}>
          <div className={styles.leftPanelHeader} />
          {rows.map((row) => (
            <div key={row} className={styles.skeletonRow}>
              <div className={styles.skeletonAvatar} />
              <div className={styles.skeletonLines}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineName}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLinePill}`} />
              </div>
            </div>
          ))}
        </div>
        <div className={styles.skeletonTimeline}>
          <div className={styles.timelineHeaders} />
          {rows.map((row) => (
            <div key={row} className={styles.skeletonTimelineBar}>
              <div className={styles.skeletonBar} style={{ left: `${5 + ((row * 17) % 40)}%`, width: `${15 + ((row * 11) % 25)}%` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The scroll container with the left panel and the timeline in it. It is the only thing on the screen that scrolls,
 * so the left panel and the headers stay in place with plain `position: sticky`.
 */
function PlannerScroller() {
  const { actions, viewStore } = usePlannerEngine();
  const viewType = useView((state) => state.viewType);
  const centerOnNextLayout = useView((state) => state.centerOnNextLayout);
  const scrollRef = useRef(null);

  // The scroll container is known to the engine, and the planner opens on today. This comes before the viewport is
  // read for the first time, so the first thing the data controller hears is the days around today and not the
  // start of the timeline, twenty years back.
  useLayoutEffect(() => {
    actions.registerScroller(scrollRef.current);
    actions.goToToday();
    return () => actions.registerScroller(null);
  }, [actions]);

  useViewportSync(scrollRef);

  // A new view has other day widths: once it is laid out, the day that was in the middle goes back there.
  useLayoutEffect(() => {
    if (centerOnNextLayout === null) return;

    actions.goToDay(centerOnNextLayout);
    viewStore.setState((state) => ({ ...state, centerOnNextLayout: null }));
  }, [centerOnNextLayout, viewType, actions, viewStore]);

  return (
    <div className={styles.scrollWrapper} ref={scrollRef}>
      <div className={styles.scrollInner}>
        <PlannerLeftPanel />
        <PlannerTimeline />
      </div>
    </div>
  );
}

function PlannerBody() {
  const { actions } = usePlannerEngine();
  const status = useData((state) => state.status);
  const resourceCount = useData((state) => state.data.order.length);

  if (status === "idle" || status === "loading") return <LoadingSkeleton />;

  if (status === "error") {
    return (
      <div className={styles.emptyState}>
        <UsersIcon />
        <p>The planner could not be loaded.</p>
        <CustomButton btnClass="todayBtn" label="Retry" clickHandler={actions.reload} />
      </div>
    );
  }

  if (resourceCount === 0) {
    return (
      <div className={styles.emptyState}>
        <UsersIcon />
        <p>No resources found for the selected filters.</p>
      </div>
    );
  }

  return (
    <RowModelProvider>
      <PlannerScroller />
    </RowModelProvider>
  );
}

/** The task form, filled in with the dates of a drag across the timeline when it creates a task. */
function TaskModal() {
  const { actions } = usePlannerEngine();
  const taskModal = useUi((state) => state.taskModal);

  const plannerAllocationData = useMemo(() => {
    if (taskModal.taskId || !taskModal.resource || !taskModal.startDate || !taskModal.endDate) return null;

    const hoursPerDay = 8;
    const days = countWeekdays(isoToDay(taskModal.startDate), isoToDay(taskModal.endDate));
    const { resource } = taskModal;

    return [
      {
        resource: {
          title: resource.title || "",
          phoneNumber: resource.phoneNumber || "",
          isExternal: resource.isExternal || false,
          isActive: resource.isActive !== undefined ? resource.isActive : true,
          email: resource.email || "",
          image: resource.image || "",
          id: resource.id,
          name: resource.name,
        },
        startDate: taskModal.startDate,
        endDate: taskModal.endDate,
        hoursPerDay,
        totalHours: parseFloat((days * hoursPerDay).toFixed(2)),
        isIncludeWeekends: false,
        isDirty: true,
      },
    ];
  }, [taskModal]);

  if (!taskModal.isOpen) return null;

  return (
    <CreateEditTask
      isOpen={taskModal.isOpen}
      isEdit={taskModal.isEdit}
      taskId={taskModal.taskId}
      projectID={taskModal.project?.id}
      projectName={taskModal.project?.name}
      scopeID={taskModal.scope?.id}
      scopeName={taskModal.scope?.name}
      initialTaskGroup={taskModal.group ? { label: taskModal.group.name, value: taskModal.group.id } : null}
      onClose={actions.closeTaskModal}
      onSave={actions.dataChanged}
      plannerAllocationData={plannerAllocationData}
    />
  );
}

/** Everything that opens over the planner: allocation conflicts, Resource Insights, quick views and the task form. */
function Overlays() {
  const { actions } = usePlannerEngine();
  const conflictsToasts = useUi((state) => state.conflictsToasts);
  const floatingCards = useUi((state) => state.floatingCards);

  return (
    <>
      {conflictsToasts.map((toast, index) => (
        <ConflictsToast key={index} data={toast} index={index} onClose={() => actions.removeConflictsToast(index)} />
      ))}

      <PlannerResourceInsights />

      {floatingCards.map((card) => (
        <FloatingAllocationCard
          key={card.id}
          id={card.id}
          task={card.task}
          scope={card.scope}
          project={card.project}
          allocation={card.allocation}
          position={card.position}
          onClose={actions.closeFloatingCard}
        />
      ))}

      <TaskModal />
    </>
  );
}

/** The resource planner: resources and the tasks they are allocated to on a timeline that scrolls sideways as far as the user cares to go. */
export default function ResourcesPlanner() {
  return (
    <PlannerProvider>
      <div className={styles.plannerContainer}>
        <PlannerHeader />
        <div className={styles.plannerBody}>
          <PlannerBody />
        </div>
        <Overlays />
      </div>
    </PlannerProvider>
  );
}
